// The upgrade mark on a node (app#361): a node that was offered an update and has not installed
// it, or a node below its channel's floor.
//
// ⚠ This module imports nothing from `models/`, for the reason `nodeStatus.ts` states: a
// `node --experimental-strip-types` test cannot load the Supabase client.
//
// ⚠⚠ "BEHIND" IS THE ROUTER'S OWN RECORD, NOT A GUESS (Benoit, 2026-09-18). The router writes an
// `upgrades` row when it OFFERS a node an update, and closes it (`completed_at`) when the node
// registers at that version. So a pending row past the grace period is a fact: offered, not
// installed. No offer means no row, which is why nothing is marked while an update is still
// rolling out, by construction. The app never recomputes the rollout.
//
// ⚠⚠ THE TEXT NAMES A CAUSE ONLY WHEN THE NODE REPORTED ONE (Benoit, 2026-09-18: "do not mention
// auto-upgrade unless it is the root cause"). The cause is `upgrades.outcome`, which the agent
// sends (router#1625). An older agent sends none, and then the text says only what happened.
//
// The floor copies the router's `Channel.HoldsBelowFloor` (router#1618): the channel is the
// node's, then its network's, then the default (`db.EffectiveChannel`).

export type ChannelLike = {
  id: string
  isDefault?: boolean
  minVersion?: string | null
}

export type UpgradeLike = {
  toVersion: string
  createdAt: string
  completedAt?: string | null
  outcome?: string | null
  reason?: string | null
  outcomeAt?: string | null
}

// A node's channel: its own, then its network's, then the default. An id that names no loaded
// channel falls through, as `GetChannelById` answering nil does in the router.
export const effectiveChannel = <C extends ChannelLike>(
  nodeChannelId: string | null | undefined,
  networkChannelId: string | null | undefined,
  channels: readonly C[]
): C | undefined => {
  const byId = (id: string | null | undefined) => (id ? channels.find(channel => channel.id === id) : undefined)
  return byId(nodeChannelId) ?? byId(networkChannelId) ?? channels.find(channel => channel.isDefault)
}

type Core = [number, number, number]

// MAJOR.MINOR.PATCH of a version, or undefined. The pre-release part is dropped:
// `build_version` is git-describe output, and `v0.4.139-3-gabc1234` is 3 commits AFTER 0.4.139.
// The router reads the floor the same way.
export const versionCore = (version: string | null | undefined): Core | undefined => {
  const match = /^v?(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/.exec((version ?? '').trim())
  if (!match) return undefined
  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

const compareCore = (a: Core, b: Core): number => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]

const isDevBuild = (core: Core) => core[0] === 0 && core[1] === 0 && core[2] === 0

// How long after the first offer, or after the node reconnects, before a node that has not
// installed is marked. An offer rides the metrics push (about 1 minute) and an install takes up
// to about 2 minutes (76 s measured on a Pi Zero, 2026-09-18).
export const UPGRADE_GRACE_MS = 3600_000

export type VersionMark = {
  kind: 'held' | 'behind'
  title: string
  detail: string
}

const stamp = (value: string | null | undefined): number => {
  const t = value ? new Date(value).getTime() : NaN
  return Number.isNaN(t) ? 0 : t
}

// ⚠ Not every node has `sudo`: on Windows the same command runs from an administrator prompt,
// as the agent's own log says.
const FIX = 'On the node, run `sudo nobgp upgrade` (on Windows, `nobgp upgrade` as an administrator).'

// What the node said about the offer, as a sentence, or '' when it said nothing about THIS
// version. `created_at` resets when the router moves the node to a new version, so an outcome
// reported before it answers an older offer.
export const upgradeCause = (upgrade: UpgradeLike | undefined): string => {
  if (!upgrade?.outcome || stamp(upgrade.outcomeAt) < stamp(upgrade.createdAt)) return ''
  const reason = (upgrade.reason ?? '').trim()
  switch (upgrade.outcome) {
    case 'auto_upgrade_off':
      // ⚠ There is no `nobgp config --auto-upgrade` flag: the key is set in the profile's config
      // file only, so that is the remedy to name (agent common/upgrade.go).
      return `Auto-upgrade is turned off on this node. ${FIX} To turn auto-upgrade on, set \`auto-upgrade: true\` in the node's config file and restart the nobgp service.`
    case 'install_failed':
      return `The install failed${reason ? `: ${reason}` : ''}. ${FIX}`
    default:
      return `The node did not install it${reason ? `: ${reason}` : ''}. ${FIX}`
  }
}

type MarkInput = {
  version: string | null | undefined
  channel: ChannelLike | undefined
  // The node's PENDING upgrade record (`completed_at` NULL), if the router offered one.
  upgrade: UpgradeLike | undefined
  offline: boolean
  onlineAt: string | null | undefined
  now: number
}

// The mark for one node, or null for none. See app#361.
export const nodeVersionMark = ({ version, channel, upgrade, offline, onlineAt, now }: MarkInput): VersionMark | null => {
  const current = versionCore(version)
  if (!current || isDevBuild(current)) return null

  // Below the floor: the router holds the node out of the network until it upgrades
  // (router#1618). Shown online or offline, because a held node reads offline.
  const floor = versionCore(channel?.minVersion)
  if (floor && compareCore(current, floor) < 0) {
    return {
      kind: 'held',
      title: 'Blocked until updated',
      detail: `This node runs ${version}, which is below the minimum version for its channel (${channel?.minVersion}). It cannot reach other nodes until it is updated. ${upgradeCause(upgrade) || FIX}`,
    }
  }

  // Offered and not installed: a pending record the node is still below.
  if (!upgrade || upgrade.completedAt) return null
  const offered = versionCore(upgrade.toVersion)
  if (!offered || compareCore(current, offered) >= 0) return null

  // ⚠ Offline: no mark. The node cannot take an offer until it reconnects, and the first metrics
  // push after that brings one. The row already says it is offline.
  if (offline) return null

  // The grace runs from the LATER of the first offer and the node's reconnect: a node back after a
  // week has had no time to take the offer.
  const since = Math.max(stamp(upgrade.createdAt), stamp(onlineAt))
  if (now - since < UPGRADE_GRACE_MS) return null

  return {
    kind: 'behind',
    title: 'Update not installed',
    detail: `This node runs ${version} and was offered ${upgrade.toVersion}, which it has not installed. ${upgradeCause(upgrade) || FIX}`,
  }
}
